import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import {fetchData} from "../../../hook/axios.hook";

const initialState = {
  productGroups: [],
  productGroup: null,
  productGroupFilters: null,
  productTypes: [],
  productType: null,
  loading: false,
  error: false,
};

//*** Operations ***

//** Product Group **
export const fetchAllVedProductGroups = createAsyncThunk("vedSettings/fetchAllVedProductGroups", async params =>
  fetchData("/productGroups", "get", {params}),
);
export const fetchVedProductGroup = createAsyncThunk("vedSettings/fetchVedProductGroup", async groupId =>
  fetchData(`/productGroups/${groupId}`, "get"),
);
export const fetchVedProductGroupFilters = createAsyncThunk(
  "vedSettings/fetchVedProductGroupFilters",
  async ({groupId, typeId} = {}) => fetchData(`/productGroups/getFilters`, "get", {params: {groupId, typeId}}),
);
export const createVedProductGroup = createAsyncThunk("vedSettings/createVedProductGroup", async body =>
  fetchData(`/productGroups`, "post", body),
);
export const updateVedProductGroup = createAsyncThunk("vedSettings/updateVedProductGroup", async ({groupId, typeId}) =>
  fetchData(`/productGroups/${groupId}/${typeId}`, "patch"),
);
export const deleteVedProductGroup = createAsyncThunk("vedSettings/deleteVedProductGroup", async groupId =>
  fetchData(`/productGroups/${groupId}`, "delete"),
);
export const unlinkProductGroupType = createAsyncThunk(
  "vedSettings/unlinkProductGroupType",
  async ({groupId, typeId}) => fetchData(`/productGroups/${groupId}/${typeId}`, "delete"),
);

//** Product Type **
export const fetchAllVedProductTypes = createAsyncThunk("vedSettings/fetchAllVedProductTypes", async params =>
  fetchData("/productTypes", "get", {params}),
);
export const fetchVedProductType = createAsyncThunk("vedSettings/fetchVedProductType", async typeId =>
  fetchData(`/productTypes/${typeId}`, "get"),
);
export const createVedProductType = createAsyncThunk("vedSettings/createVedProductType", async body =>
  fetchData(`/productTypes`, "post", body),
);
export const deleteVedProductType = createAsyncThunk("vedSettings/deleteVedProductType", async typeId =>
  fetchData(`/productTypes/${typeId}`, "delete"),
);

export const VED_SETTINGS_STORE_NAME = "vedSettings";

const VedSettingsSlice = createSlice({
  name: VED_SETTINGS_STORE_NAME,
  initialState,
  reducers: {
    clearProductGroup: state => {
      state.productGroup = null;
    },
  },
  extraReducers: builder => {
    builder
      //** Product Group **
      .addCase(fetchAllVedProductGroups.fulfilled, (state, {payload}) => {
        state.productGroups = payload;
      })
      .addCase(fetchVedProductGroup.fulfilled, (state, {payload}) => {
        state.productGroup = payload;
      })
      .addCase(fetchVedProductGroupFilters.fulfilled, (state, {payload}) => {
        state.productGroupFilters = payload;
      })
      .addCase(createVedProductGroup.fulfilled, (state, {payload}) => {
        state.productGroup = payload;
        state.productGroups = [payload, ...state.productGroups];
      })
      .addCase(updateVedProductGroup.fulfilled, (state, {payload}) => {
        state.productGroup = payload;

        state.productGroups = state.productGroups.map(productGroup =>
          productGroup.productGroupId === payload.productGroupId ? payload : productGroup,
        );
      })
      .addCase(deleteVedProductGroup.fulfilled, (state, {meta}) => {
        state.productGroup = null;
        state.productGroups = state.productGroups.filter(({productGroupId}) => productGroupId !== meta.arg);
      })
      .addCase(unlinkProductGroupType.fulfilled, (state, {meta}) => {
        state.productGroups = state.productGroups.map(productGroup => {
          if (productGroup.productGroupId === Number(meta.arg.groupId)) {
            productGroup.productTypes = productGroup.productTypes.filter(
              ({productTypeId}) => productTypeId !== Number(meta.arg.typeId),
            );
          }
          return productGroup;
        });
      })
      //** Product Type **
      .addCase(fetchAllVedProductTypes.fulfilled, (state, {payload}) => {
        state.productTypes = payload;
      })
      .addCase(fetchVedProductType.fulfilled, (state, {payload}) => {
        state.productType = payload;
      })
      .addCase(createVedProductType.fulfilled, (state, {payload}) => {
        state.productType = payload;
        // state.productTypes = [payload, ...state.productTypes];
      })
      .addCase(deleteVedProductType.fulfilled, (state, {meta}) => {
        state.productType = null;
        state.productTypes = state.productTypes.filter(({productTypeId}) => productTypeId !== meta.arg);
      })

      //fulfilled matcher
      .addMatcher(
        action => action.type.startsWith(VED_SETTINGS_STORE_NAME) && action.type.endsWith("/fulfilled"),
        state => handleFulfilled(state),
      )
      //pending matcher
      .addMatcher(
        action => action.type.startsWith(VED_SETTINGS_STORE_NAME) && action.type.endsWith("/pending"),
        state => handlePending(state),
      )
      //rejected matcher
      .addMatcher(
        action => action.type.startsWith(VED_SETTINGS_STORE_NAME) && action.type.endsWith("/rejected"),
        (state, {error}) => handleRejected(state, error),
      )
      //default
      .addDefaultCase(() => {});
  },
});

function handleFulfilled(state) {
  state.loading = false;
  state.error = false;
}

function handlePending(state) {
  state.loading = true;
  state.error = false;
}

function handleRejected(state, error) {
  state.loading = false;
  state.error = error;
}

const {actions, reducer} = VedSettingsSlice;

export const {clearProductGroup} = actions;

export default reducer;

// *** Selectors ***
export const selectVedProductGroups = state => state[VED_SETTINGS_STORE_NAME].productGroups;
export const selectVedProductGroup = state => state[VED_SETTINGS_STORE_NAME].productGroup;
export const selectVedProductGroupFilters = state => state[VED_SETTINGS_STORE_NAME].productGroupFilters;
export const selectVedProductTypes = state => state[VED_SETTINGS_STORE_NAME].productTypes;
export const selectVedProductType = state => state[VED_SETTINGS_STORE_NAME].productType;
export const selectVedSettingsLoading = state => state[VED_SETTINGS_STORE_NAME].loading;
